import { ANALYTICS_QUERY_GC_TIME } from "@/constants";
import { useAnalyticsApiClient } from "@/context/AnalyticsQueryLoaderProvider";
import { DateHelper } from "@/lib/dates";
import { getLastInvoiceMonth } from "@/utils/dates";
import { useQueries } from "@tanstack/react-query";
import {
  DataSource,
  DurationType,
  Operator,
} from "@ternary/api-lib/analytics/enums";
import { billingCucSchema } from "@ternary/api-lib/analytics/schemas/billingCUC";
import { billingFocusSchema } from "@ternary/api-lib/analytics/schemas/billingFOCUS";
import { QueryFilter } from "@ternary/api-lib/analytics/types";
import { Dimension } from "@ternary/api-lib/analytics/ui/types";
import {
  FiscalPeriodMap,
  getFiscalLastMTDDateRange,
} from "@ternary/api-lib/analytics/utils/FiscalDateUtils";
import { startOfMonth } from "date-fns";
import UError from "unilib-error";
import useGatekeeper from "../../hooks/useGatekeeper";
import { UseQueryOptions, UseQueryResult } from "../../lib/react-query";
import { SpendSummaryEntity } from "./types";
import { buildDataQuery, useGetDataQueryContext } from "./utils/useDataQuery";

type SpendSummary = {
  [billingFocusSchema.measures.absoluteCredits.schemaName]: number | null;
  [billingFocusSchema.measures.billedCost.schemaName]: number | null;
};

export interface Params {
  dataSource: DataSource;
  dimensions?: Dimension[];
  excludeCredits?: boolean;
  fiscalPeriodMap?: FiscalPeriodMap | null;
  isFiscalMode?: boolean;
  queryFilters?: QueryFilter[];
}

type UseQueriesOptions = [
  [SpendSummaryEntity, UError],
  [SpendSummaryEntity, UError],
  [SpendSummaryEntity, UError],
];

/**
 * Determines the appropriate cost measure key based on the data source.
 * Returns the schema name for the cost measure in either the billingCUC or billingFOCUS schema.
 * BE expects and handles "cost" measure key for non FOCUS_BILLING data sources.
 * @param params - Object containing dataSource to determine which schema to use
 * @returns The schema name string for the cost measure
 */
function getCostMeasureKey(params: { dataSource: DataSource }): string {
  return params.dataSource === DataSource.FOCUS_BILLING
    ? billingFocusSchema.measures.billedCost.schemaName
    : billingCucSchema.measures.cost.schemaName;
}

export default function useGetSpendSummaries(
  params: Params,
  options?: UseQueryOptions<SpendSummaryEntity, UError>
): UseQueryResult<SpendSummaryEntity, UError>[] {
  const client = useAnalyticsApiClient();
  const gatekeeper = useGatekeeper();
  const { tenantID, queryContext } = useGetDataQueryContext();

  const dateHelper = new DateHelper();

  const { authenticatedUser } = queryContext;
  const fiscalPeriodMap = authenticatedUser.tenant.fiscalCalendar?.periods;
  const isFiscalMode = authenticatedUser.settings.fiscalMode;

  const enabled = gatekeeper.hasConfiguredDataIntegration;

  // BE expects and handles "cost" measure key for non FOCUS_BILLING data sources.
  const measures =
    params.dataSource === DataSource.FOCUS_BILLING
      ? params.excludeCredits
        ? [billingFocusSchema.measures.billedCost]
        : [
            billingFocusSchema.measures.billedCost,
            billingFocusSchema.measures.absoluteCredits,
          ]
      : [billingCucSchema.measures.cost];

  return useQueries<UseQueriesOptions>({
    queries: [
      // This MTD
      {
        queryFn: async () => {
          const result = await client.loadData<SpendSummary>(
            tenantID,
            buildDataQuery(
              {
                ...params,
                dateRange: [dateHelper.firstOfMonth(), dateHelper.date],
                durationType: DurationType.MONTH_TO_DATE,
                isFiscalMode,
                measures,
              },
              queryContext
            )
          );

          const summary = result.response[0];

          const absoluteCredits =
            summary[billingFocusSchema.measures.absoluteCredits.schemaName] ??
            0;
          const billedCost = summary[getCostMeasureKey(params)] ?? 0;

          return {
            absoluteCredits,
            billedCost,
            grossCost: absoluteCredits + billedCost,
            name: "Current MTD",
          };
        },
        queryKey: ["spendSummary", "currentMTD", params],
        gcTime: ANALYTICS_QUERY_GC_TIME,
        enabled,
        ...options,
      },
      // Last Month Full
      {
        queryFn: async () => {
          const result = await client.loadData<SpendSummary>(
            tenantID,
            buildDataQuery(
              {
                ...params,
                dateRange: [
                  dateHelper.firstOfLastMonthMinusThreeDays(),
                  dateHelper.lastDayLastMonthPlusThreeDays(),
                ],
                isFiscalMode,
                measures,
                ...(isFiscalMode
                  ? { durationType: DurationType.LAST_THIRTY_DAYS }
                  : {
                      queryFilters: [
                        ...(params.dataSource === DataSource.FOCUS_BILLING
                          ? [
                              {
                                schemaName:
                                  billingFocusSchema.dimensions.invoiceMonth
                                    .schemaName,
                                operator: Operator.EQUALS,
                                values: [getLastInvoiceMonth()],
                              },
                            ]
                          : []),
                        ...(params.queryFilters ? params.queryFilters : []),
                      ],
                    }),
              },
              queryContext
            )
          );

          const summary = result.response[0];

          const absoluteCredits =
            summary[billingFocusSchema.measures.absoluteCredits.schemaName] ??
            0;
          const billedCost = summary[getCostMeasureKey(params)] ?? 0;

          return {
            absoluteCredits,
            billedCost,
            grossCost: absoluteCredits + billedCost,
            name: "Last Month Full",
          };
        },
        queryKey: ["spendSummary", "lastMonthFull", params],
        gcTime: ANALYTICS_QUERY_GC_TIME,
        enabled,
        ...options,
      },
      // Last MTD
      {
        queryFn: async () => {
          const fiscalLastMTDDateRange = getFiscalLastMTDDateRange(
            fiscalPeriodMap ?? {}
          );

          const result = await client.loadData<SpendSummary>(
            tenantID,
            buildDataQuery(
              {
                ...params,
                dateRange:
                  isFiscalMode && fiscalLastMTDDateRange
                    ? fiscalLastMTDDateRange
                    : [
                        startOfMonth(dateHelper.sameDayLastMonth()),
                        dateHelper.sameDayLastMonth(),
                      ],
                isFiscalMode,
                measures,
              },
              queryContext
            )
          );

          const summary = result.response[0];

          const absoluteCredits =
            summary[billingFocusSchema.measures.absoluteCredits.schemaName] ??
            0;
          const billedCost = summary[getCostMeasureKey(params)] ?? 0;

          return {
            absoluteCredits,
            billedCost,
            grossCost: absoluteCredits + billedCost,
            name: "Last MTD",
          };
        },
        queryKey: ["spendSummary", "lastMTD", params],
        gcTime: ANALYTICS_QUERY_GC_TIME,
        enabled,
        ...options,
      },
    ],
  });
}
